import { useSelector } from 'react-redux';
import { ExpandableList, IconName } from '@optimus-web/core';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { useFeatureFlag } from '@mews/framework';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import {
    isCurrentProfileAdmin,
    hasCurrentProfilePlatformScope,
    canCurrentProfileManageEnterpriseEmployees,
    canCurrentProfileConfigureEnterprise,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { hasCurrentEnterpriseSsoAccess } from '@mews-commander/legacy-entities/src/selectors/session/enterpriseProductSubscriptions';
import { hasPlatformScopeWithAccessAllEnterprises } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { canManagePortfolioRoles } from '../../roles/privileges';
import { createListItemWithLink } from '../helpers';
import AnalyticsMenu from './AnalyticsMenu';
import AnalyticsReportingMenu from './AnalyticsReportingMenu';
import { EnterpriseRateManagementMenu } from './EnterpriseRatesMenu';
import ExportsMenu from './ExportsMenu';
import { CommonMenuItems } from './MenuItems';
import { PortfolioQueueMenu } from './PortfolioQueueMenu';
import { PortfolioUsersMenu } from './PortfolioUsersMenu';
import { EnterprisePoliciesMenu } from './EnterprisePoliciesMenu';
import BIMenuItem from './BIMenuItem';

const PortfolioMenu = () => {
    const canConfigureEnterprise: boolean = useSelector(canCurrentProfileConfigureEnterprise);
    const enterpriseId = useCurrentEnterpriseId();
    const isAdmin: boolean = useSelector(isCurrentProfileAdmin);
    const isCentralProductsEnabled = useFeatureFlag(FeatureFlag.centralProductsEnabled);
    const canAccessAllEnterprises: boolean = useSelector(hasPlatformScopeWithAccessAllEnterprises);
    const hasPlatformScope: boolean = useSelector(hasCurrentProfilePlatformScope);
    const showUsers: boolean = useSelector(canCurrentProfileManageEnterpriseEmployees);
    const showRoles: boolean = useSelector(canManagePortfolioRoles);
    const hasSsoAccess = !!useSelector(hasCurrentEnterpriseSsoAccess);

    return (
        <>
            {canConfigureEnterprise || (hasPlatformScope && canAccessAllEnterprises) || isAdmin ? (
                <>
                    {createListItemWithLink({
                        textKey: TextKey.PropertiesNavigation,
                        iconName: IconName.Property,
                        path: 'EnterpriseRelationshipReport/Index',
                        tooltip: null,
                        isMainItem: true,
                    })}

                    {createListItemWithLink({
                        textKey: TextKey.PropertyGroupsNavigation,
                        iconName: IconName.FolderOpened,
                        path: 'EnterpriseRelationshipReport/Groups',
                    })}

                    {(showUsers || showRoles) && (
                        <ExpandableList>
                            <PortfolioUsersMenu
                                currentEnterpriseId={enterpriseId}
                                showUsers={showUsers}
                                showRoles={showRoles}
                                showGroups={hasSsoAccess}
                            />
                        </ExpandableList>
                    )}

                    {createListItemWithLink({
                        textKey: TextKey.PortfolioSettings,
                        iconName: IconName.Settings,
                        path: 'Enterprise/Detail/$enterpriseId',
                        params: { enterpriseId },
                        tooltip: null,
                        isMainItem: true,
                    })}

                    <ExpandableList>
                        <EnterpriseRateManagementMenu />
                    </ExpandableList>

                    <ExpandableList>
                        <EnterprisePoliciesMenu value="enterprise-policies" />
                    </ExpandableList>

                    {isCentralProductsEnabled
                        ? createListItemWithLink({
                              textKey: TextKey.Products,
                              iconName: IconName.Product,
                              path: 'EnterpriseProducts/Index',
                              tooltip: null,
                              isMainItem: true,
                          })
                        : null}

                    {createListItemWithLink({
                        textKey: TextKey.TaskPlural,
                        iconName: IconName.Tasks,
                        path: 'TaskReport/Index',
                        isMainItem: true,
                    })}

                    <ExpandableList>
                        <ExportsMenu value="exports" />
                    </ExpandableList>
                    <ExpandableList>
                        <PortfolioQueueMenu value="portfolio-queue" />
                    </ExpandableList>
                    <BIMenuItem />
                    <ExpandableList>
                        <AnalyticsMenu enterpriseId={enterpriseId} value="analytics" />
                    </ExpandableList>
                    <ExpandableList>
                        <AnalyticsReportingMenu enterpriseId={enterpriseId} value="analytics-reporting" />
                    </ExpandableList>
                </>
            ) : null}
            <CommonMenuItems />
        </>
    );
};

export default PortfolioMenu;
